import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import PaginatedTable from './PaginatedTable';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';
import { type AlpacaEvent } from '../models/AlpacaEvent';
import useMediaQuery from '../helpers/useMediaQuery';
import { format } from 'date-fns';
import MobileList from './MobileList';
import { Link } from 'react-router';

export default function AlpacaEventTable({
  events,
}: {
  events: AlpacaEvent[];
}) {
  const matches = useMediaQuery('screen and (max-width: 640px)');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => events, [events]);
  const columnData: ColumnDef<AppTableFeatures, AlpacaEvent>[] = useMemo(
    () => [
      {
        header: 'Date',
        accessorFn: (d) =>
          d?.event?.date
            ? format(new Date(d.event.date ?? ''), 'MM/dd/yyyy')
            : '',
        sortFn: (a, b, _, desc = true) => {
          const aDate = new Date(a.original.event?.date ?? '');
          const bDate = new Date(b.original.event?.date ?? '');
          if (desc) {
            return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
          }
          return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
        },
        filterFn: 'includesString',
        size: 150,
        cell: ({ row }) => (
          <Link
            className='font-bold hover:opacity-70 dark:text-gray-300'
            to={`/events/${row.original.eventId}`}
          >
            {format(new Date(row.original.event?.date ?? ''), 'MM/dd/yyyy')}
          </Link>
        ),
      },
      {
        header: 'Type',
        accessorFn: (d) =>
          d.event?.eventTypeId == 7
            ? d.event.otherEventName
            : d.event?.eventType?.eventTypeName,
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'Note',
        accessorKey: 'note',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        cell: ({ row }) => (
          <div className='whitespace-pre-line'>{row.original.note}</div>
        ),
      },
      {
        header: 'Medication',
        accessorKey: 'event.medication',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'Contact',
        accessorFn: (d) => d.event?.contact?.name ?? '',
        sortFn: 'datetime',
        filterFn: 'equals',
        size: 175,
        cell: ({ row }) => (
          <Link
            className='font-bold hover:opacity-70 dark:text-gray-300'
            to={`/contacts/${row.original?.event?.contactId}`}
          >
            {row.original.event?.contact?.name ?? ''}
          </Link>
        ),
      },
    ],
    [],
  );

  const columns = useMemo(() => columnData, [columnData]);

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: events.length > 9 ? 10 : events.length,
      },
    },
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    paginateExpandedRows: false,
  });

  if (matches) {
    return (
      <MobileList
        table={table}
        columnResizeMode='onChange'
        showFilters={true}
        showPagination={events.length > 9}
      />
    );
  }
  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
      showPagination={events.length > 9}
    />
  );
}
